import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ResourceCategoryTypes } from "@enums/resource-category-types";
import { SelectOption } from "@idw-types/common";
import { ThemingGenericContentSlotValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { AXNUILayoutHelpTextInner } from "@iddataweb/idw-common-ui/types";
import { FileInputFormGroup } from "../../../../../../common/file-input-form-group/file-input-form-group";
import { SelectFormGroup } from "../../../../../../common/select-form-group/select-form-group";
import { InputFormGroup } from "../../../../../../common/input-form-group/input-form-group";

type ContentSlotImageContainerProps = {
    formContentSlotType: string;
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    validationErrors: ThemingGenericContentSlotValidationErrors;
    resourceCategoryType: ResourceCategoryTypes;
    imageUrl: string;
    defaultImageUrl: string;
    imagePosition: string;
    defaultImagePosition: string;
    imageAltText: string;
    defaultImageAltText: string;
    imageWidth: string;
    defaultImageWidth: string;
    imageHeight: string;
    defaultImageHeight: string;
    uploadFileToS3: (file: File, category: ResourceCategoryTypes) => Promise<string>;
    updateFormContentSlotContentField: (contentField: Partial<AXNUILayoutHelpTextInner>) => void;
};

export const ContentSlotImageContainer = (props: ContentSlotImageContainerProps) => {
    const {
        formContentSlotType,
        layout,
        labelWidth,
        isDisabled,
        validationErrors,
        resourceCategoryType,
        imageUrl,
        defaultImageUrl,
        imagePosition,
        defaultImagePosition,
        imageAltText,
        defaultImageAltText,
        imageWidth,
        defaultImageWidth,
        imageHeight,
        defaultImageHeight,
        uploadFileToS3,
        updateFormContentSlotContentField
    } = props;

    const [contentSlotImageUploadInProgress, setContentSlotImageUploadInProgress] = useState(false);

    const handleContentSlotImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
            //upload to s3
            setContentSlotImageUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, resourceCategoryType);
            if (uploadResult) {
                updateFormContentSlotContentField({
                    imageUrl: uploadResult
                });
            }
            setContentSlotImageUploadInProgress(false);
        }
    };

    const handleResetContentSlotImage = () => {
        updateFormContentSlotContentField({
            imageUrl: defaultImageUrl
        });
    };

    const handleContentSlotImagePositionChange = (newValue: SelectOption) => {
        updateFormContentSlotContentField({ alignImage: newValue.value });
    };

    const handleResetContentSlotImagePosition = () => {
        updateFormContentSlotContentField({ alignImage: defaultImagePosition });

    };

    const handleContentSlotImageAltTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateFormContentSlotContentField({ imageAltText: event.target.value });
    };

    const handleResetContentSlotImageAltText = () => {
        updateFormContentSlotContentField({ imageAltText: defaultImageAltText });

    };

    const handleContentSlotImageWidthChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateFormContentSlotContentField({ imageWidth: event.target.value });
    };

    const handleResetContentSlotImageWidth = () => {
        updateFormContentSlotContentField({ imageWidth: defaultImageWidth });
    };

    const handleContentSlotImageHeightChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateFormContentSlotContentField({ imageHeight: event.target.value });
    };

    const handleResetContentSlotImageHeight = () => {
        updateFormContentSlotContentField({ imageHeight: defaultImageHeight });
    };

    const contentSlotImagePositionOptions = useMemo(() => {
        return [
            { label: "center", value: "center" },
            { label: "left", value: "left" },
            { label: "right", value: "right" },
        ];
    }, []);

    const selectedContentSlotImagePositionOption = useMemo(() => {
        if (imagePosition) {
            return contentSlotImagePositionOptions.find(po => po.label === imagePosition);
        }
        return null;
    }, [imagePosition]);

    return (
        <div>
            <FileInputFormGroup
                controlId={"content-slot-image-input"}
                automationId={"content-slot-image-input"}
                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.image.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.image.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handleContentSlotImageChange}
                value={imageUrl || ""}
                accept={"image/png, image/gif, image/jpeg, image/jpg"}
                disabled={isDisabled}
                uploadInProgress={contentSlotImageUploadInProgress}
                isRequired={true}
                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.image.tooltip(formContentSlotType)}
                afterLabelIcon={isDisabled ? null : faUndo}
                afterLabelIconOnClick={handleResetContentSlotImage}
            />
            {validationErrors.imageUrlError}
            <SelectFormGroup
                controlId={"content-slot-text-postition-select-input"}
                automationId={"content-slot-text-position-select-input"}
                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imagePosition.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imagePosition.label}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handleContentSlotImagePositionChange}
                options={contentSlotImagePositionOptions}
                selectedOption={selectedContentSlotImagePositionOption}
                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imagePosition.tooltip(formContentSlotType)}
                afterLabelIcon={isDisabled ? null : faUndo}
                afterLabelIconOnClick={handleResetContentSlotImagePosition}
            />
            <InputFormGroup
                controlId={"content-slot-image-width-text-input"}
                automationId={"content-slot-image-width-text-input"}
                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageWidth.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageWidth.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handleContentSlotImageWidthChange}
                value={imageWidth || ""}
                disabled={isDisabled}
                isRequired={true}
                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageWidth.tooltip}
                afterLabelIcon={isDisabled ? null : faUndo}
                afterLabelIconOnClick={handleResetContentSlotImageWidth}
            />
            {validationErrors.imageWidthError}
            <InputFormGroup
                controlId={"content-slot-image-height-text-input"}
                automationId={"content-slot-image-height-text-input"}
                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageHeight.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageHeight.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handleContentSlotImageHeightChange}
                value={imageHeight || ""}
                disabled={isDisabled}
                isRequired={true}
                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageHeight.tooltip}
                afterLabelIcon={isDisabled ? null : faUndo}
                afterLabelIconOnClick={handleResetContentSlotImageHeight}
            />
            {validationErrors.imageHeightError}
            <InputFormGroup
                controlId={"content-slot-image-alt-text-input"}
                automationId={"content-slot-image-alt-text-input"}
                label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageAltText.label}
                ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageAltText.label}
                layout={layout}
                labelWidth={labelWidth}
                onChange={handleContentSlotImageAltTextChange}
                value={imageAltText || ""}
                disabled={isDisabled}
                isRequired={false}
                afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.common.inputs.imageAltText.tooltip}
                afterLabelIcon={isDisabled ? null : faUndo}
                afterLabelIconOnClick={handleResetContentSlotImageAltText}
            />
        </div>
    );
};